// src/services/roleService.js

import api from '../fapis/client';
import endpoints from '../fapis/endpoints';

// ============================================================
// HELPER: Format error message
// ============================================================
const formatErrorMessage = (error) => {
  if (error.message === 'Failed to fetch' || error.code === 'ERR_NETWORK') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request timed out. Please try again.';
  }
  
  if (error.response) {
    return error.response.data?.detail || error.response.data?.message || error.message;
  }
  
  return error.message || 'An unexpected error occurred. Please try again.';
};

// ============================================================
// ROLE SERVICE
// ============================================================
export const roleService = {
  /**
   * Get all roles for the current company
   * @returns {Promise<{success: boolean, data?: Array, message?: string, status?: number}>}
   */
  getRoles: async () => {
    try {
      const response = await api.get(endpoints.retailone.roles);
      return {
        success: true,
        data: response.data || [],
        status: response.status
      };
    } catch (error) {
      console.error('❌ Get roles error:', error);
      
      let message = formatErrorMessage(error);
      let status = error.status || 500;
      
      if (error.response?.status === 403) {
        message = 'You do not have permission to view roles.';
      }
      
      return {
        success: false,
        message: message,
        status: status,
        data: []
      };
    }
  },

  /**
   * Get a single role by UUID
   * @param {string} uuid - Role UUID
   * @returns {Promise<{success: boolean, data?: Object, message?: string, status?: number}>}
   */
  getRole: async (uuid) => {
    if (!uuid) {
      return {
        success: false,
        message: 'Role ID is required',
        status: 400
      };
    }

    try {
      const response = await api.get(`${endpoints.retailone.roles}${uuid}`);
      return {
        success: true,
        data: response.data || null,
        status: response.status
      };
    } catch (error) {
      console.error(`❌ Get role ${uuid} error:`, error);
      
      let message = formatErrorMessage(error);
      let status = error.status || 500;
      
      if (error.response?.status === 404) {
        message = 'Role not found';
      } else if (error.response?.status === 403) {
        message = 'You do not have permission to view this role.';
      }
      
      return {
        success: false,
        message: message,
        status: status,
        data: null
      };
    }
  }
};

export default roleService;